import { createTheme, ColorSystemOptions } from '@mui/material/styles'
import { LuCircleCheck, LuOctagonAlert, LuTriangleAlert, LuInfo } from 'react-icons/lu'
import { Z_INDEX } from './constants'

const colorConstants: ColorSystemOptions['palette'] = {
  secondary: {
    main: '#E66B00',
    light: '#FF9822',
    dark: '#C94300',
  },
  primary: {
    main: '#00BBD8',
    contrastText: '#ffffff',
  },
  node: {
    main: '#00BBD8',
    service: '#00BBD8',
    contrastText: '#ffffff',
  },
  success: {
    main: '#6EB116',
    contrastText: '#ffffff',
  },
  info: {
    main: '#00BBD8',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#E66B00',
    contrastText: '#ffffff',
  },
  error: {
    main: '#dc0f00',
    contrastText: '#ffffff',
  },
}

// Create your Material UI theme
const theme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        iconWeight: 1.3,
        background: {
          default: '#ffffff',
          paper: '#fafafa',
        },
        contrast: {
          max: '#000000',
          high: '#615E5C',
          medium: '#9D9A99',
          low: '#CECBC9',
          minimal: '#e7e7e7',
          paper: '#fafafa',
        },
        stream: {
          main: '#9D9A99', //'#69d6e7', //'#308e9c', //'#69d6e7',
        },
        info: {
          main: '#615E5C',
          light: '#F6F6F6',
        },
        transparent: {
          main: '#00000006',
          light: '#F6F6F699',
          lighten: '#FFFFFFAA',
        },
        ...colorConstants,
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        iconWeight: 1,
        background: {
          default: '#000000',
          paper: '#141414',
        },
        contrast: {
          max: '#ffffff',
          high: '#d7d8d8',
          medium: '#9EA1A3',
          low: '#626566',
          minimal: '#262829',
          paper: '#141414',
        },
        stream: {
          main: '#006170',
        },
        info: {
          main: '#d7d8d8',
          light: '#1b1b1b',
        },
        transparent: {
          main: '#FFFFFF08',
          light: '#26282999',
          lighten: '#000000AA',
        },
        ...colorConstants,
      },
    },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", sans-serif',
    // ⚠⚠ A HEADING SIZE MUST LIVE HERE, NOT ONLY IN components.MuiTypography. The two
    // readers do not share a source: `<Typography variant="h1">` picks up the component
    // styleOverride below, and `sx={{ typography: 'h1' }}` reads THIS object. While h1
    // was styled only as an override, `theme.typography.h1` stayed MUI's stock 6rem at
    // weight 300 — so the node and network page titles, which reach h1 through `sx` on
    // an input slot, rendered 3.3x too large and too light (app#178, seen in production
    // 2026-08-31). Same class as the `textSecondary` vs `text.secondary` split in
    // Node.tsx: one name, two readers, one of them blind to the app's own value.
    // ⚠ h5 and h6 are still override-only below. They take a `theme` callback, which
    // this object cannot express, and nothing reads them through `sx`. If anything ever
    // does, move them here first.
    h1: {
      fontWeight: 500,
      fontSize: '1.8rem',
      letterSpacing: -0.5,
    },
    h2: {
      fontWeight: 500,
      fontSize: '1.4rem',
    },
    code: {
      fontFamily: '"IBM Plex Mono", monospace',
      fontWeight: 500,
      fontSize: '0.8125rem', // 13px
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 8,
    borderRadiusSmall: 4,
    borderRadiusLarge: 16,
    backdropBlur: 'blur(8px)',
    borderBlur: 'blur(2px)',
  },
  zIndex: {
    drawer: Z_INDEX.DRAWER,
    modal: Z_INDEX.MODAL,
    tooltip: Z_INDEX.TOOLTIP,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        strong: {
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiListItem: {
      defaultProps: {
        disableGutters: true,
        disablePadding: true,
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          marginLeft: theme.spacing(-2),
          marginRight: theme.spacing(-2),
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: theme.spacing(4),
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.6875rem',
          paddingTop: theme.spacing(1),
          lineHeight: 1.5,
          fontWeight: 500,
          letterSpacing: 1,
          textTransform: 'uppercase',
          backgroundColor: 'transparent',
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        // h1 and h2 moved to `typography` above — one definition, both readers.
        h5: ({ theme }) => ({
          fontWeight: 400,
          fontSize: '1rem',
          fontStyle: 'italic',
          marginBottom: theme.spacing(2),
        }),
        h6: ({ theme }) => ({
          color: theme.palette.contrast.medium,
          fontWeight: 500,
          fontSize: '0.75rem', // 12px
        }),
        subtitle1: {
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.6875rem', // 11px
          letterSpacing: 1,
        },
        subtitle2: {
          fontWeight: 500,
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          letterSpacing: 1,
        },
        caption: {
          display: 'block',
          lineHeight: 1.4,
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <LuCircleCheck />,
          error: <LuOctagonAlert />,
          warning: <LuTriangleAlert />,
          // ⚠ An info icon, not a spinner (app#469). `info` is the severity of a NOTE ("Only the
          // Owner can change the plan"), and a spinner beside a note reads as "still loading". An
          // Alert that does mean "in progress" gives a CircularProgress of its own (Billing's
          // "Finalizing your subscription…", Register's read that is tried again).
          info: <LuInfo />,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.contrast.paper,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.contrast.low,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.contrast.minimal,
          },
        }),
        input: ({ theme }) => ({
          paddingTop: theme.spacing(1.5),
          paddingBottom: theme.spacing(1.5),
        }),
        sizeSmall: ({ theme }) => ({
          '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(0.75),
            paddingBottom: theme.spacing(0.75),
            fontSize: '0.8125rem',
          },
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.contrast.minimal,
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
        }),
        underline: { '&:before, &:after': { display: 'none' } },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontStyle: 'italic',
          color: theme.palette.text.disabled,
          lineHeight: 1.3,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        // The select's content takes the inherited 23px line height where an input takes 1.4375em,
        // so a Select was 4px taller than a TextField of the same size: 35px against 31px when
        // small, which put the Audit page's Action select 4.3px below its Search field.
        select: { lineHeight: '1.4375em' },
      },
    },
    MuiInputBase: {
      defaultProps: {},
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: theme.spacing(3),
          legend: { display: 'none' },
          fieldset: { top: 0 },
        }),
        multiline: { textarea: { padding: 0 } },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 500,
          color: theme.palette.contrast.medium,
          top: theme.spacing(0.8),
          left: theme.spacing(-1.6),
          '&.Mui-disabled': {
            color: theme.palette.contrast.medium,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          justifyContent: 'space-between',
          textAlign: 'left',
          fontWeight: 600,
          padding: theme.spacing(0.8, 2),
          lineHeight: 1.25,
          // '&.MuiButtonBase-root:hover': { backgroundColor: theme.palette.transparent.main },
        }),
        outlined: ({ theme }) => ({
          fontWeight: 500,
          borderColor: theme.palette.contrast.minimal,
          backgroundColor: theme.palette.contrast.paper,
        }),
        text: ({ theme }) => ({
          fontWeight: 400,
          padding: theme.spacing(0.8, 2),
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          padding: theme.spacing(1),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
        sizeSmall: ({ theme }) => ({ fontSize: '1rem', padding: theme.spacing(1) }),
        sizeMedium: { fontSize: '1.3rem' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadiusSmall,
          padding: theme.spacing(0.25),
          textTransform: 'uppercase',
          fontSize: '0.625rem',
          fontWeight: 500,
          letterSpacing: 0.5,
          height: 'auto',
        }),
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 2,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        dense: ({ theme }) => ({
          paddingX: theme.spacing(2),
          paddingY: theme.spacing(0),
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          borderColor: theme.palette.contrast.minimal,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 600,
          fontSize: theme.typography.body2.fontSize,
          textTransform: 'none',
          color: theme.palette.contrast.high,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: 0,
        },
      },
    },
    // ⚠ The button row of a dialog lines up with its text. MUI's default is 8px on each side, and
    // DialogTitle and DialogContent have 24px: the buttons sat 16px outside the text, and 9px from
    // the border of a popup on a phone, where the popup has no padding of its own.
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1, 3, 2.5),
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: 'transparent',
          backdropFilter: theme.shape.backdropBlur,
        }),
        invisible: {
          backdropFilter: 'none',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          '&::before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
  },
})

export default theme
